"use client";
import { SegmentControl } from "@/components/SegmentControl";
import BlokWrap from "@/components/bloks/BlokWrap";
import { useRouter } from "@/i18n/routing";
import { trackContentClick } from "@/lib/analytics/trackContentClick";
import { sbLink } from "@/lib/storyblok/link";
import type { BlokHero } from "@/storyblok";
import { type BlokProps, StoryblokComponent } from "@frend-digital/storyblok";
import { clsx } from "@frend-digital/ui";
import type { StoryblokMultilink } from "storyblok";
import { AssetBlok, multiAssetToSingleAsset, sizes } from "../Asset";
import styles from "./Hero.module.css";

export const HeroBlok = ({ blok, preview }: BlokProps<BlokHero>) => {
	const alignmentClass = blok.alignment === "right" ? styles.alignRight : styles.alignLeft;
	const link = sbLink(blok.link as StoryblokMultilink);
	const router = useRouter();

	const heroContent = (
		<div className={clsx(styles.root, alignmentClass)}>
			<div
				className={styles.section}
				data-height={(blok.height || undefined) as "small" | "medium" | "large" | undefined}
				data-height-mobile={
					(blok.heightMobile || undefined) as "small" | "medium" | "large" | undefined
				}
			>
				<div className={styles.content}>
					<h1 className={clsx(styles.title, blok.small && styles.small)}>{blok.title}</h1>
					<div className={styles.buttonGroup}>
						{blok.buttons?.map((button) => (
							<StoryblokComponent key={button._uid} blok={button} preview={preview} />
						))}
					</div>
				</div>
			</div>
			<div className={styles.imageContainer}>
				<AssetBlok
					lqip={blok.blurHash}
					muted
					autoPlay
					loop
					priority
					source={multiAssetToSingleAsset(blok.source)}
					quality={75}
					sizes={sizes(90, 80)}
					getUrl={(url) => `${url}/m/1600x0`}
				/>
			</div>
		</div>
	);

	if (link?.href) {
		return (
			<SegmentControl segments={blok}>
				<BlokWrap editable={blok} asChild>
					{/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
					<div
						className="unstyled"
						onClick={() => {
							trackContentClick({
								contentType: blok.component,
								itemId: blok._uid,
								href: link.href,
								label: blok.title,
							});
							router.push(link.href);
						}}
						// biome-ignore lint/a11y/useSemanticElements: <explanation>
						role="button"
						tabIndex={0}
					>
						{heroContent}
					</div>
				</BlokWrap>
			</SegmentControl>
		);
	}

	return (
		<SegmentControl segments={blok}>
			<BlokWrap editable={blok} asChild>
				{heroContent}
			</BlokWrap>
		</SegmentControl>
	);
};
